/**
 * Appointment Configuration Parser
 * Handles parsing and processing of appointment configuration data
 */

export class AppointmentConfigParser {
    constructor(configData) {
        this.rawConfig = configData;
        this.config = {
            minAdvanceBooking: 3,
            excludedDates: new Set(),
            slotDuration: 60,
            weekSchedule: {}
        };

        this.parse();
    }

    /**
     * Parse the configuration data
     */
    parse() {
        if (!this.rawConfig) {
            // console.warn('No configuration data provided');
            return;
        }

        this.parseMinAdvanceBooking();
        this.parseExcludedDates();
        this.parseSlotDuration();
        this.parseWeekSchedule();

        // console.log('Parsed appointment configuration:', this.config);
    }

    /**
     * Parse minimum advance booking days
     */
    parseMinAdvanceBooking() {
        const minAdvance = this.rawConfig.min_advance_booking;

        if (minAdvance !== null && minAdvance !== undefined && !isNaN(minAdvance)) {
            this.config.minAdvanceBooking = parseInt(minAdvance);
        } else {
            this.config.minAdvanceBooking = 3; // Default
        }
    }

    /**
     * Parse excluded dates
     * Format: "06/11/2025,\n11/11/2025" or "06/11/2025, 11/11/2025"
     */
    parseExcludedDates() {
        const excludedDatesRaw = this.rawConfig.excluded_dates;

        if (!excludedDatesRaw) {
            this.config.excludedDates = new Set();
            return;
        }

        try {
            // Split by comma or newline
            const dateStrings = excludedDatesRaw
                .toString()
                .split(/[,\n]+/)
                .map(date => date.trim())
                .filter(date => date.length > 0);

            // Convert DD/MM/YYYY to YYYY-MM-DD
            const convertedDates = dateStrings.map(dateStr => {
                return this.convertDateFormat(dateStr);
            }).filter(date => date !== null);

            this.config.excludedDates = new Set(convertedDates);

            // console.log('Excluded dates parsed:', Array.from(this.config.excludedDates));
        } catch (error) {
            // console.error('Error parsing excluded dates:', error);
            this.config.excludedDates = new Set();
        }
    }

    /**
     * Convert date from DD/MM/YYYY to YYYY-MM-DD
     */
    convertDateFormat(dateStr) {
        try {
            const parts = dateStr.split('/');
            if (parts.length !== 3) return null;

            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];

            return `${year}-${month}-${day}`;
        } catch (error) {
            // console.error('Error converting date format:', dateStr, error);
            return null;
        }
    }

    /**
     * Parse slot duration in minutes
     */
    parseSlotDuration() {
        const duration = this.rawConfig.slot_duration;

        if (duration !== null && duration !== undefined && !isNaN(duration)) {
            this.config.slotDuration = parseInt(duration);
        } else {
            this.config.slotDuration = 60; // Default: 1 hour
        }
    }

    /**
     * Parse week schedule (opening days and hours)
     */
    parseWeekSchedule() {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

        days.forEach((dayName, dayIndex) => {
            const isOpen = this.rawConfig[`${dayName}_open`];
            const hoursStr = this.rawConfig[`${dayName}_hours`];

            this.config.weekSchedule[dayIndex] = {
                name: dayName,
                open: isOpen === true || isOpen === 'true' || isOpen === 1,
                hours: []
            };

            // Parse hours if day is open
            if (this.config.weekSchedule[dayIndex].open && hoursStr) {
                this.config.weekSchedule[dayIndex].hours = this.parseHours(hoursStr);
            }
        });

        // console.log('Week schedule parsed:', this.config.weekSchedule);
    }

    /**
     * Parse hours string
     * Format: "08:30-12:00,13:00-16:00" or "08:30-12:00, 13:00-16h00"
     */
    parseHours(hoursStr) {
        if (!hoursStr) return [];

        try {
            const ranges = hoursStr
                .toString()
                .split(',')
                .map(range => range.trim())
                .filter(range => range.length > 0);

            const parsedRanges = ranges.map(range => {
                // Handle both ":" and "h" formats
                const cleanRange = range.replace(/h/g, ':');
                const parts = cleanRange.split('-');

                if (parts.length !== 2) return null;

                const start = this.normalizeTime(parts[0].trim());
                const end = this.normalizeTime(parts[1].trim());

                if (!start || !end) return null;

                return { start, end };
            }).filter(range => range !== null);

            return parsedRanges;
        } catch (error) {
            // console.error('Error parsing hours:', hoursStr, error);
            return [];
        }
    }

    /**
     * Normalize time format to HH:MM
     */
    normalizeTime(timeStr) {
        try {
            // Handle formats like "8:30", "08:30", "8h30"
            const cleaned = timeStr.replace(/h/g, ':');
            const parts = cleaned.split(':');

            if (parts.length !== 2) return null;

            const hours = parts[0].padStart(2, '0');
            const minutes = parts[1].padStart(2, '0');

            return `${hours}:${minutes}`;
        } catch (error) {
            // console.error('Error normalizing time:', timeStr, error);
            return null;
        }
    }

    /**
     * Check if a date is excluded
     */
    isDateExcluded(date) {
        const dateStr = this.formatDateToString(date);
        return this.config.excludedDates.has(dateStr);
    }

    /**
     * Check if a day of week is open
     */
    isDayOpen(dayOfWeek) {
        const schedule = this.config.weekSchedule[dayOfWeek];
        return schedule && schedule.open;
    }

    /**
     * Get hours for a specific day of week
     */
    getHoursForDay(dayOfWeek) {
        const schedule = this.config.weekSchedule[dayOfWeek];
        return schedule && schedule.open ? schedule.hours : [];
    }

    /**
     * Generate time slots for a specific date
     */
    generateTimeSlotsForDate(date) {
        const dayOfWeek = date.getDay();
        const hours = this.getHoursForDay(dayOfWeek);

        if (!hours || hours.length === 0) {
            return [];
        }

        const slots = [];

        hours.forEach(range => {
            const rangeSlots = this.generateSlotsForRange(range.start, range.end);
            slots.push(...rangeSlots);
        });

        return slots;
    }

    /**
     * Generate time slots for a specific time range
     */
    generateSlotsForRange(startTime, endTime) {
        const slots = [];
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);

        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        const slotDuration = this.config.slotDuration;

        for (let minutes = startMinutes; minutes < endMinutes; minutes += slotDuration) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;

            slots.push({
                time: timeStr,
                startMinutes: minutes,
                endMinutes: minutes + slotDuration
            });
        }

        return slots;
    }

    /**
     * Format date to YYYY-MM-DD string
     */
    formatDateToString(date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Get minimum booking date
     */
    getMinBookingDate() {
        const today = new Date();
        const minDate = new Date(today);
        minDate.setDate(today.getDate() + this.config.minAdvanceBooking);
        return minDate;
    }

    /**
     * Get configuration object
     */
    getConfig() {
        return this.config;
    }
}

/**
 * Factory function to create parser from element
 */
export function createConfigParserFromElement(elementId) {
    const element = document.getElementById(elementId);
    if (!element) {
        // console.error('Calendar element not found:', elementId);
        return null;
    }

    const configAttr = element.dataset.config;
    if (!configAttr) {
        // console.warn('No config data attribute found on element');
        return new AppointmentConfigParser({});
    }

    try {
        const configData = JSON.parse(configAttr);
        return new AppointmentConfigParser(configData);
    } catch (error) {
        // console.error('Error parsing config JSON:', error);
        return new AppointmentConfigParser({});
    }
}
